import { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { parseLightMarkdown, type InlineNode } from './lightMarkdown';

function Inline({ nodes }: { nodes: InlineNode[] }) {
  return (
    <>
      {nodes.map((node, index) => {
        switch (node.kind) {
          case 'text':
            return <Fragment key={index}>{node.text}</Fragment>;
          case 'bold':
            return (
              <strong key={index}>
                <Inline nodes={node.children} />
              </strong>
            );
          case 'link':
            return node.internal ? (
              <Link key={index} to={node.href}>
                {node.text}
              </Link>
            ) : (
              <a key={index} href={node.href} target="_blank" rel="noopener noreferrer">
                {node.text}
              </a>
            );
        }
      })}
    </>
  );
}

/**
 * An assistant message, drawn from parsed nodes into React elements. There is
 * no HTML anywhere on this path, so a model that writes `<script>` writes text.
 */
export function AssistantText({ content }: { content: string }) {
  const blocks = useMemo(() => parseLightMarkdown(content), [content]);

  return (
    <div className="assistant-text">
      {blocks.map((block, index) => {
        switch (block.kind) {
          case 'paragraph':
            return (
              <p key={index}>
                {block.lines.map((line, lineIndex) => (
                  <Fragment key={lineIndex}>
                    {lineIndex > 0 ? <br /> : null}
                    <Inline nodes={line} />
                  </Fragment>
                ))}
              </p>
            );
          case 'heading':
            return (
              <p key={index} className="assistant-text__heading">
                <Inline nodes={block.content} />
              </p>
            );
          case 'list': {
            const items = block.items.map((item, itemIndex) => (
              <li key={itemIndex}>
                <Inline nodes={item} />
              </li>
            ));
            return block.ordered ? <ol key={index}>{items}</ol> : <ul key={index}>{items}</ul>;
          }
        }
      })}
    </div>
  );
}
